import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router-dom'

import {
  PERIODES,
  agregerSiDense,
  distributionBristol,
  facettesSymptomes,
  fenetreDe,
  ingredientsPopulaires,
  recettesPopulaires,
  repasParType,
  resumeSelles,
  sellesParJour,
  type Periode,
} from '../../analysis/statistiques'
import { db } from '../../db/db'
import { dayKeyOf } from '../../domain/time'
import {
  BRISTOL_COURT,
  MEAL_TYPE_LABELS,
  SYMPTOM_LABELS,
  type MealType,
} from '../../domain/types'
import { Carte, Entete, Vide } from '../composants'
import { BarresHorizontales, BarresVerticales, Figure, NuageFacette } from '../Graphiques'

const FMT_JOUR = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' })

export function PageStatistiques() {
  const [periode, setPeriode] = useState<Periode>(90)

  const donnees = useLiveQuery(
    async () => ({
      repas: await db.meals.toArray(),
      selles: await db.stools.toArray(),
      sensations: await db.sensations.toArray(),
      recettes: await db.recipes.toArray(),
      ingredients: await db.ingredients.toArray(),
    }),
    [],
  )

  const vue = useMemo(() => {
    if (!donnees) return undefined

    const instants = [
      ...donnees.repas.map((m) => m.at),
      ...donnees.selles.map((s) => s.at),
      ...donnees.sensations.map((s) => s.at),
    ].sort()
    const premier = instants.length ? dayKeyOf(instants[0]!) : null
    const f = fenetreDe(periode, premier)

    const serieBrute = sellesParJour(donnees.selles, f)
    const { serie, pas } = agregerSiDense(serieBrute)

    return {
      f,
      serie,
      pas,
      resume: resumeSelles(serieBrute),
      bristol: distributionBristol(donnees.selles, f),
      facettes: facettesSymptomes(donnees.sensations, donnees.selles, f),
      recettes: recettesPopulaires(donnees.repas, donnees.recettes, f),
      ingredients: ingredientsPopulaires(donnees.repas, donnees.ingredients, f),
      parType: repasParType(donnees.repas, f),
      nbRepas: donnees.repas.filter((m) => {
        const j = dayKeyOf(m.at)
        return j >= f.debut && j <= f.fin
      }).length,
    }
  }, [donnees, periode])

  if (!vue) {
    return (
      <>
        <Entete titre="Statistiques" />
        <main className="contenu">
          <p className="doux">Chargement...</p>
        </main>
      </>
    )
  }

  const { f, serie, pas, resume, bristol, facettes, recettes, ingredients, parType, nbRepas } = vue
  const totalBristol = bristol.reduce((s, b) => s + b.n, 0)
  const bornes = facettes.flatMap((fa) => fa.points.map((p) => p.t))
  const tMin = bornes.length ? Math.min(...bornes) : 0
  const tMax = bornes.length ? Math.max(...bornes) : 1

  return (
    <>
      <Entete titre="Statistiques" sousTitre={`${f.jours} jours · ${nbRepas} repas`} />

      <main className="contenu">
        <div className="segments">
          <Link to="/analyses" className="segment">
            Corrélations
          </Link>
          <span className="segment" aria-current="page">
            Statistiques
          </span>
        </div>

        {/* Un seul filtre, au-dessus de tout ce qu'il cadre. */}
        <div className="puces" role="group" aria-label="Période affichée">
          {PERIODES.map((p) => (
            <button
              key={p.valeur}
              type="button"
              className="choix"
              aria-pressed={periode === p.valeur}
              onClick={() => setPeriode(p.valeur)}
            >
              {p.libelle}
            </button>
          ))}
        </div>

        {/* --- Transit --- */}
        <Carte titre="Transit">
          <div className="ligne" style={{ gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
            <Stat valeur={resume.total} libelle="passages" />
            <Stat valeur={resume.moyenneParJour.toFixed(1)} libelle="par jour en moyenne" />
            <Stat valeur={resume.joursSansPassage} libelle="jours sans passage" />
          </div>

          <Figure
            titre={pas === 'jour' ? 'Passages par jour' : 'Passages par semaine'}
            description={
              pas === 'semaine'
                ? 'Regroupé par semaine : la période est trop longue pour une barre par jour.'
                : 'Les jours sans passage valent zéro et restent affichés.'
            }
            tableau={
              <table className="table-contingence">
                <thead>
                  <tr>
                    <th scope="col">{pas === 'jour' ? 'Jour' : 'Semaine du'}</th>
                    <th scope="col">Passages</th>
                  </tr>
                </thead>
                <tbody>
                  {serie
                    .filter((p) => p.valeur > 0)
                    .map((p) => (
                      <tr key={p.date}>
                        <th scope="row">{FMT_JOUR.format(new Date(`${p.date}T12:00:00`))}</th>
                        <td>{p.valeur}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            }
          >
            <BarresVerticales
              points={serie.map((p) => ({
                cle: p.date,
                libelle: p.libelle,
                valeur: p.valeur,
                info: `${FMT_JOUR.format(new Date(`${p.date}T12:00:00`))} : ${p.valeur} passage(s)`,
              }))}
              labelY="Nombre de passages aux selles par jour"
            />
          </Figure>

          <Figure
            titre="Consistance (échelle de Bristol)"
            description="1-2 dures, 3-5 normales, 6-7 molles. La position sur l'axe porte l'identité ; la couleur ne fait que la rappeler."
            tableau={
              <table className="table-contingence">
                <thead>
                  <tr>
                    <th scope="col">Bristol</th>
                    <th scope="col">Description</th>
                    <th scope="col">Passages</th>
                    <th scope="col">Part</th>
                  </tr>
                </thead>
                <tbody>
                  {bristol.map((b) => (
                    <tr key={b.bristol}>
                      <th scope="row">{b.bristol}</th>
                      <td>{BRISTOL_COURT[b.bristol as 1]}</td>
                      <td>{b.n}</td>
                      <td>{totalBristol ? `${Math.round((b.n / totalBristol) * 100)} %` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <BarresVerticales
              points={bristol.map((b) => ({
                cle: String(b.bristol),
                libelle: String(b.bristol),
                valeur: b.n,
                couleur:
                  b.categorie === 'dur'
                    ? 'var(--viz-dur)'
                    : b.categorie === 'mou'
                      ? 'var(--viz-mou)'
                      : 'var(--viz-normal)',
                info: `Bristol ${b.bristol} — ${BRISTOL_COURT[b.bristol as 1]} : ${b.n} passage(s)`,
              }))}
              hauteur={130}
              labelY="Répartition des passages selon l'échelle de Bristol"
            />
            <div className="legende viz">
              <span className="legende__item">
                <span className="legende__marque" style={{ background: 'var(--viz-dur)' }} />
                Dures (1-2)
              </span>
              <span className="legende__item">
                <span className="legende__marque" style={{ background: 'var(--viz-normal)' }} />
                Normales (3-5)
              </span>
              <span className="legende__item">
                <span className="legende__marque" style={{ background: 'var(--viz-mou)' }} />
                Molles (6-7)
              </span>
            </div>
          </Figure>
        </Carte>

        {/* --- Symptômes dans le temps --- */}
        <Carte titre="Symptômes dans le temps">
          {facettes.length === 0 ? (
            <Vide>Aucun symptôme enregistré sur la période.</Vide>
          ) : (
            <>
              <p className="doux" style={{ marginBottom: 14 }}>
                Un graphique par symptôme, sur une même échelle de temps : hauteur du point =
                intensité ressentie. Séparer plutôt que colorer permet de rester lisible pour les
                daltonismes, où plus de trois couleurs deviennent indistinguables dans un nuage.
              </p>
              <div className="pile">
                {facettes.map((fa) => (
                  <div key={fa.type} className="facette">
                    <div className="facette__titre">
                      <span>{SYMPTOM_LABELS[fa.type]}</span>
                      <span className="tres-doux mono">{fa.points.length}</span>
                    </div>
                    <NuageFacette
                      points={fa.points.map((p) => ({
                        t: p.t,
                        y: p.intensite,
                        info: `${new Date(p.t).toLocaleString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })} — ${p.info}`,
                      }))}
                      tMin={tMin}
                      tMax={tMax}
                    />
                  </div>
                ))}
              </div>
              <div className="ligne ligne--espacee tres-doux" style={{ marginTop: 8 }}>
                <span>{FMT_JOUR.format(new Date(tMin))}</span>
                <span>{FMT_JOUR.format(new Date(tMax))}</span>
              </div>
            </>
          )}
        </Carte>

        {/* --- Repas populaires --- */}
        <Carte titre="Repas les plus fréquents">
          {nbRepas === 0 ? (
            <Vide>Aucun repas enregistré sur la période.</Vide>
          ) : (
            <>
              {recettes.length > 0 && (
                <Figure
                  titre="Recettes"
                  description="Nombre de repas où la recette a été utilisée."
                  tableau={<TableauPopularite lignes={recettes} entete="Recette" />}
                >
                  <BarresHorizontales
                    points={recettes.map((r) => ({ cle: r.id, libelle: r.nom, valeur: r.n }))}
                  />
                </Figure>
              )}

              <Figure
                titre="Ingrédients"
                description="Nombre de repas contenant l'ingrédient, recettes développées."
                tableau={<TableauPopularite lignes={ingredients} entete="Ingrédient" />}
              >
                <BarresHorizontales
                  points={ingredients.map((i) => ({ cle: i.id, libelle: i.nom, valeur: i.n }))}
                />
              </Figure>

              <Figure
                titre="Moments de la journée"
                description="Répartition des repas enregistrés."
                tableau={
                  <table className="table-contingence">
                    <thead>
                      <tr>
                        <th scope="col">Moment</th>
                        <th scope="col">Repas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...parType.entries()].map(([t, n]) => (
                        <tr key={t}>
                          <th scope="row">{MEAL_TYPE_LABELS[t as MealType]}</th>
                          <td>{n}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              >
                <BarresHorizontales
                  points={[...parType.entries()]
                    .sort((a, b) => b[1] - a[1])
                    .map(([t, n]) => ({
                      cle: t,
                      libelle: MEAL_TYPE_LABELS[t as MealType],
                      valeur: n,
                    }))}
                />
              </Figure>
            </>
          )}
        </Carte>

        <p className="tres-doux centre">
          Ces chiffres décrivent ce qui a été enregistré. Ils ne cherchent aucun lien de cause à
          effet : c'est le rôle de l'onglet Corrélations.
        </p>
      </main>
    </>
  )
}

function Stat({ valeur, libelle }: { valeur: number | string; libelle: string }) {
  return (
    <div>
      {/* Chiffres proportionnels : `tabular-nums` ferait respirer les grands nombres. */}
      <div className="gras" style={{ fontSize: '1.5rem', lineHeight: 1.1, color: 'var(--brun)' }}>
        {valeur}
      </div>
      <div className="tres-doux">{libelle}</div>
    </div>
  )
}

function TableauPopularite({
  lignes,
  entete,
}: {
  lignes: { id: string; nom: string; n: number; part: number }[]
  entete: string
}) {
  return (
    <table className="table-contingence">
      <thead>
        <tr>
          <th scope="col">{entete}</th>
          <th scope="col">Repas</th>
          <th scope="col">Part</th>
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => (
          <tr key={l.id}>
            <th scope="row">{l.nom}</th>
            <td>{l.n}</td>
            <td>{Math.round(l.part * 100)} %</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
